import type { ComputedRef, Ref } from 'vue';
import { computed, nextTick, ref, toRef } from 'vue';
import { useRoute } from 'vue-router';

import type { IStatusDescriptionOptions } from '@modules/affiliates-profile/composables';
import type { IApiFormErrors } from '@modules/core/services/query/types';
import type { EmployeeEditFormSchema, EmployeeEditItem, EmployeeEditItemResponse } from '@modules/employees/submodules/edit/types';
import type { FormGroup, FormSchema } from '@modules/forms/components/VForm/types';
import type { IFormSelectInputOption } from '@modules/forms/types';
import { useStatusDescription } from '@modules/affiliates-profile/composables';
import { useConfirmDialogs } from '@modules/core/composables/dialogs';
import { EMPLOYEES_EDIT_PERMISSIONS, TRANSLATION_GROUP_EMPLOYEES_EDIT } from '@modules/employees/submodules/edit/constants';
import { useEmployeeEditMutation, useEmployeeResetMfaMutation } from '@modules/employees/submodules/edit/mutations';
import { useEmployeeEditQuery } from '@modules/employees/submodules/edit/queries';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { PERMISSIONS_QUERY_KEY } from '@modules/permissions/constants';
import { usePermissionsStore } from '@modules/permissions/store';
import { useUserStore } from '@modules/user/store';
import { useQueryClient } from '@tanstack/vue-query';
import { assert, isNumber } from '@utils/guards';

interface EmployeeEditPageComposable {
  isLoadingQuery: Ref<boolean>;
  isSaveLoading: Ref<boolean>;
  formSchema: ComputedRef<FormSchema<EmployeeEditFormSchema>>;
  formGroups: Ref<FormGroup<EmployeeEditFormSchema>[]>;
  errors: Ref<IApiFormErrors | undefined>;
  clearError(key: string): void;
  handleSubmit(data: EmployeeEditFormSchema): void;
}

export default function useEmployeeEditPage(): EmployeeEditPageComposable {
  const { getTranslation } = useI18n();
  const route = useRoute();
  const { confirmEdit } = useConfirmDialogs();
  const user = toRef(useUserStore(), 'user');
  const { hasPermission } = usePermissionsStore();
  const queryClient = useQueryClient();
  const isSaveLoading = ref(false);

  const id = computed<number | undefined>(() => (
    route.params.id ? Number(route.params.id) : undefined
  ));

  const editMfaPermission = computed(() => hasPermission(EMPLOYEES_EDIT_PERMISSIONS.CLEAR_GOOGLE_SECRET).value);

  const { data: employeeResponse, isLoading: isLoadingQuery, setData } = useEmployeeEditQuery(id);

  const profile = computed<EmployeeEditItem | undefined>(() => employeeResponse.value?.personal);

  const statusOptions = computed<IStatusDescriptionOptions>(() => ({
    date: profile.value?.ban_status_chaged_at,
    name: profile.value?.ban_status_name,
    translationKey: TRANSLATION_GROUP_EMPLOYEES_EDIT,
    shortCode: 'STATUS_DESCRIPTION',
    dateProp: 'ban_status_chaged_at',
    nameProp: 'ban_status_name',
  }));

  const { description } = useStatusDescription(statusOptions);

  const {
    data: responseEditData,
    mutateAsync: onEdit,
    errors,
    clearError,
  } = useEmployeeEditMutation();

  const { mutateAsync: resetMfa } = useEmployeeResetMfaMutation();

  const rolesOptions = computed<IFormSelectInputOption<number>[]>(() => {
    if (!employeeResponse.value?.roles) {
      return [];
    }

    return employeeResponse.value?.roles.map((item) => ({
      value: item.id,
      label: `${item.name}`,
    }));
  });

  function updateEmployeeQueryData(queryData: EmployeeEditItemResponse | undefined, values: Partial<EmployeeEditFormSchema>): EmployeeEditItemResponse | undefined {
    if (!queryData) {
      return queryData;
    }

    return {
      ...queryData,
      personal: {
        ...queryData.personal,
        ...values,
      },
    };
  }

  async function saveEmployee(data: EmployeeEditFormSchema): Promise<void> {
    assert(isNumber(id.value));

    try {
      await onEdit({ id: id.value, personal: data });

      if (profile.value?.role_id !== data.role_id && profile.value?.id === user.value?.id) {
        await queryClient.invalidateQueries({ queryKey: [PERMISSIONS_QUERY_KEY] });
      }

      if (editMfaPermission.value && profile.value?.mfa_enabled && data.mfa_enabled === false) {
        try {
          await resetMfa(id.value);

          setData((queryData) => {
            return updateEmployeeQueryData(queryData, { mfa_enabled: data.mfa_enabled });
          });
        } catch {}
      }

      if (responseEditData.value?.personal) {
        // Server response can be the same as previous one and vue does not update form, so first of all set payload as query data
        setData((queryData) => {
          return updateEmployeeQueryData(queryData, { ...data });
        });
        await nextTick();
        // Then set server response to trigger vue reactivity
        setData((queryData) => {
          return updateEmployeeQueryData(queryData, responseEditData.value.personal);
        });
      }
    } catch {}
  }

  function handleSubmit(data: EmployeeEditFormSchema): void {
    confirmEdit({
      header: getTranslation(TRANSLATION_GROUP_EMPLOYEES_EDIT, 'TITLE').value,
      message: getTranslation(TRANSLATION_GROUP_EMPLOYEES_EDIT, 'MESSAGE').value,
      acceptLabel: getTranslation(TRANSLATION_GROUP_EMPLOYEES_EDIT, 'ANSWER_YES').value,
      rejectLabel: getTranslation(TRANSLATION_GROUP_EMPLOYEES_EDIT, 'ANSWER_NO').value,
      accept: () => {
        isSaveLoading.value = true;
        void saveEmployee(data).finally(() => {
          isSaveLoading.value = false;
        });
      },
    });
  }

  const formSchema = computed<FormSchema<EmployeeEditFormSchema>>(() => {
    const schema: FormSchema<EmployeeEditFormSchema> = {
      name: {
        type: EFormInputTypes.TEXT,
        label: getTranslation(TRANSLATION_GROUP_EMPLOYEES_EDIT, 'NAME').value,
        required: true,
        value: profile.value?.name,
      },
      ms_teams: {
        type: EFormInputTypes.TEXT,
        label: getTranslation(TRANSLATION_GROUP_EMPLOYEES_EDIT, 'MS_TEAMS').value,
        value: profile.value?.ms_teams,
      },
      telegram: {
        type: EFormInputTypes.TEXT,
        label: getTranslation(TRANSLATION_GROUP_EMPLOYEES_EDIT, 'TELEGRAM').value,
        value: profile.value?.telegram,
      },
      whatsapp: {
        type: EFormInputTypes.TEXT,
        label: getTranslation(TRANSLATION_GROUP_EMPLOYEES_EDIT, 'WHATSAPP').value,
        value: profile.value?.whatsapp,
      },
      email: {
        type: EFormInputTypes.TEXT,
        label: getTranslation(TRANSLATION_GROUP_EMPLOYEES_EDIT, 'EMAIL').value,
        required: true,
        value: profile.value?.email,
      },
      role_id: {
        type: EFormInputTypes.SELECT,
        label: getTranslation(TRANSLATION_GROUP_EMPLOYEES_EDIT, 'ROLE').value,
        required: true,
        options: rolesOptions.value,
        value: profile.value?.role_id,
      },
      banned: {
        type: EFormInputTypes.SWITCH,
        label: getTranslation(TRANSLATION_GROUP_EMPLOYEES_EDIT, 'STATUS').value,
        value: !!profile.value?.banned,
        description: profile.value?.ban_status_name ? description.value : undefined,
        trueLabel: getTranslation(TRANSLATION_GROUP_EMPLOYEES_EDIT, 'LOCK').value,
        falseLabel: getTranslation(TRANSLATION_GROUP_EMPLOYEES_EDIT, 'UNLOCK').value,
      },
    };

    if (editMfaPermission.value) {
      schema.mfa_enabled = {
        type: EFormInputTypes.SWITCH,
        label: getTranslation(TRANSLATION_GROUP_EMPLOYEES_EDIT, 'MFA_ENABLED_LABEL').value,
        value: profile.value?.mfa_enabled,
        trueLabel: getTranslation(TRANSLATION_GROUP_EMPLOYEES_EDIT, 'MFA_ENABLED').value,
        falseLabel: getTranslation(TRANSLATION_GROUP_EMPLOYEES_EDIT, 'MFA_DISABLED').value,
        disabled: !profile.value?.mfa_enabled,
      };
    }

    return schema;
  });

  const formGroups = computed<FormGroup<EmployeeEditFormSchema>[]>(() => ([
    {
      items: editMfaPermission.value
        ? [
            'name',
            'ms_teams',
            'telegram',
            'whatsapp',
            'email',
            'role_id',
            'mfa_enabled',
            'banned',
          ]
        : [
            'name',
            'ms_teams',
            'telegram',
            'whatsapp',
            'email',
            'role_id',
            'banned',
          ],
    },
  ]));

  return {
    isLoadingQuery,
    isSaveLoading,
    formSchema,
    formGroups,
    errors,
    clearError,
    handleSubmit,
  };
}
